import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import monitoringService from '../../services/monitoringService';
import projectService from '../../services/projectService';
import teamService from '../../services/teamService';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleInfo } from '../team/MemberFormModal';
import { CATEGORY_PRESETS, formatCategory, getTrackedCategoryOptions } from '../inspections/categoryOptions';
import MonitoringNav from './MonitoringNav';
import './Monitoring.css';

const FREQUENCIES = ['daily', 'weekly', 'monthly', 'quarterly', 'event_based'];
const DATA_TYPES = ['number', 'text', 'boolean', 'select'];
const SEVERITIES = ['critical', 'major', 'minor'];

// Plain-language guidance for whoever is setting this up and may not know
// what "Critical"/"Major"/"Minor" is supposed to mean in practice.
const SEVERITY_GUIDANCE = {
  critical: 'Severe risk to safety, health, the environment, or legal compliance - needs action right away.',
  major: 'A real problem that needs fixing promptly, but not an immediate emergency.',
  minor: 'A small issue worth tracking, with no urgent risk.',
};

// Reuses the existing .rag-badge pill styling (Monitoring.css) instead of
// introducing a new badge style just for this.
const SEVERITY_TO_RAG_CLASS = { critical: 'red', major: 'amber', minor: 'green' };

// A sensible starting point per category so most people never have to
// guess - always shown and editable before saving, never silently forced.
const CATEGORY_SEVERITY_SUGGESTION = {
  osh: 'major',
  environmental: 'major',
  labor: 'major',
  social: 'minor',
  stakeholder: 'minor',
  biodiversity: 'major',
  culturalHeritage: 'major',
  indigenousPeoples: 'major',
  gbvPrevention: 'critical',
  childLabor: 'critical',
  permits: 'minor',
  roadSafety: 'major',
};

const emptyForm = {
  id: null,
  category: 'environmental',
  customCategory: '',
  name: '',
  description: '',
  unit: '',
  frequency: 'daily',
  dataType: 'number',
  options: '',
  targetMin: '',
  targetMax: '',
  projectId: '',
  assignedTo: '',
  defaultSeverity: CATEGORY_SEVERITY_SUGGESTION.environmental,
};

const MonitoringParameters = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { organisation } = useAuth();

  const [parameters, setParameters] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Only offer categories this org actually tracks (plus its custom ones) -
  // a parameter's existing category (even if a preset the org later stopped
  // tracking) is always kept selectable so editing never silently changes it.
  const categoryOptions = useMemo(() => (
    getTrackedCategoryOptions(
      organisation?.trackingCategories,
      organisation?.customCategories || [],
      form.category !== 'custom' ? form.category : null,
    )
  ), [organisation, form.category]);

  const fetchParameters = useCallback(async () => {
    try {
      setLoading(true);
      const params = projectFilter !== 'all' ? { projectId: projectFilter } : {};
      const data = await monitoringService.getParameters(params);
      setParameters(data.parameters || []);
    } catch (error) {
      console.error('Failed to fetch monitoring parameters:', error);
      toast.error(error.response?.data?.error || 'Failed to load monitoring parameters');
    } finally {
      setLoading(false);
    }
  }, [projectFilter]);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const data = await teamService.list();
      setMembers((data.users || []).filter((u) => u.isActive));
    } catch (error) {
      // Not fatal for viewers who can't list the team - assignment just won't be editable.
      console.error('Failed to fetch team members:', error);
    }
  }, []);

  useEffect(() => {
    fetchParameters();
    fetchProjects();
    fetchMembers();
  }, [fetchParameters, fetchProjects, fetchMembers]);

  // Support /monitoring/parameters/new opening the modal directly, optionally
  // pre-filling the category (e.g. from a Category Overview page's
  // "New Monitoring Parameter" quick action: ?category=roadSafety).
  useEffect(() => {
    if (location.pathname.endsWith('/new')) {
      const categoryParam = new URLSearchParams(location.search).get('category');
      if (categoryParam) {
        const isPreset = CATEGORY_PRESETS.includes(categoryParam);
        setForm({
          ...emptyForm,
          category: isPreset ? categoryParam : 'custom',
          customCategory: isPreset ? '' : categoryParam,
        });
      } else {
        setForm(emptyForm);
      }
      setShowModal(true);
    }
  }, [location.pathname, location.search]);

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm);
    if (location.pathname.endsWith('/new')) {
      navigate('/monitoring/parameters', { replace: true });
    }
  };

  const openCreateModal = () => {
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (parameter) => {
    const isPreset = CATEGORY_PRESETS.includes(parameter.category);
    setForm({
      id: parameter.id,
      category: isPreset ? parameter.category : 'custom',
      customCategory: isPreset ? '' : parameter.category,
      name: parameter.name,
      description: parameter.description || '',
      unit: parameter.unit || '',
      frequency: parameter.frequency,
      dataType: parameter.dataType,
      options: (parameter.options || []).join(', '),
      targetMin: parameter.targetMin ?? '',
      targetMax: parameter.targetMax ?? '',
      projectId: parameter.projectId || '',
      assignedTo: parameter.assignedTo || '',
      defaultSeverity: parameter.defaultSeverity || '',
    });
    setShowModal(true);
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Re-suggest a severity when the category changes, but only while
  // creating a brand-new parameter - an existing parameter's severity is a
  // deliberate choice someone already made and shouldn't be silently
  // overwritten just because they changed the category on edit.
  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setForm((prev) => ({
      ...prev,
      category,
      ...(prev.id ? {} : { defaultSeverity: CATEGORY_SEVERITY_SUGGESTION[category] || 'major' }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const category = form.category === 'custom' ? form.customCategory.trim() : form.category;
    if (!category) {
      toast.error('Please provide a category name');
      return;
    }
    if (!form.name.trim()) {
      toast.error('Please provide a parameter name');
      return;
    }

    const payload = {
      category,
      name: form.name.trim(),
      description: form.description || null,
      unit: form.unit || null,
      frequency: form.frequency,
      dataType: form.dataType,
      options: form.dataType === 'select'
        ? form.options.split(',').map((o) => o.trim()).filter(Boolean)
        : [],
      targetMin: form.targetMin === '' ? null : form.targetMin,
      targetMax: form.targetMax === '' ? null : form.targetMax,
      projectId: form.projectId || null,
      assignedTo: form.assignedTo || null,
      defaultSeverity: form.defaultSeverity || null,
    };

    try {
      setSaving(true);
      if (form.id) {
        await monitoringService.updateParameter(form.id, payload);
        toast.success('Monitoring parameter updated');
      } else {
        await monitoringService.createParameter(payload);
        toast.success('Monitoring parameter created');
      }
      closeModal();
      fetchParameters();
    } catch (error) {
      console.error('Failed to save monitoring parameter:', error);
      toast.error(error.response?.data?.error || 'Failed to save monitoring parameter');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (parameter) => {
    try {
      await monitoringService.updateParameter(parameter.id, { isActive: !parameter.isActive });
      toast.success(parameter.isActive ? 'Parameter deactivated' : 'Parameter reactivated');
      fetchParameters();
    } catch (error) {
      console.error('Failed to update parameter:', error);
      toast.error(error.response?.data?.error || 'Failed to update parameter');
    }
  };

  const handleDelete = async (parameter) => {
    if (!window.confirm(`Delete "${parameter.name}"? This cannot be undone.`)) return;
    try {
      await monitoringService.deleteParameter(parameter.id);
      toast.success('Monitoring parameter deleted');
      fetchParameters();
    } catch (error) {
      console.error('Failed to delete parameter:', error);
      toast.error(error.response?.data?.error || 'Failed to delete parameter');
    }
  };

  return (
    <div className="monitoring-page">
      <div className="monitoring-header">
        <div>
          <h1><i className="fas fa-shield-alt"></i> Monitoring</h1>
          <p className="header-subtitle">Configure what gets checked during Inspections and Daily Log entries</p>
        </div>
        <button className="btn-primary-green" onClick={openCreateModal}>
          <i className="fas fa-plus"></i> New Parameter
        </button>
      </div>

      {/* Plain-language explainer for whoever lands here fresh (e.g. straight
          from onboarding) with no prior context for what a "parameter" is
          or why they're being asked to configure one before they can do
          much else. */}
      <div className="monitoring-explainer">
        <i className="fas fa-circle-info"></i>
        <p>
          <strong>How this works:</strong> each parameter you add here becomes a checklist item whenever someone runs
          an Inspection for that category (e.g. "Noise Level" under Environmental), or something they can log a
          reading against in Daily Log. Without at least one active parameter per category, there's nothing to
          inspect or log yet.
        </p>
      </div>

      <MonitoringNav />

      <div className="monitoring-controls">
        <select className="filter-select" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
          <option value="all">All (org-wide + project-scoped)</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading monitoring parameters...</h3>
        </div>
      ) : parameters.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-sliders-h"></i>
          <h3>No monitoring parameters configured yet</h3>
          <p>
            Define what you want to track day to day - e.g. Noise Level, Water pH, or PPE availability. Each one
            becomes a checklist item the next time someone inspects that category, so this is usually the first
            thing to set up after picking your compliance categories.
          </p>
          <button className="btn-text" onClick={openCreateModal}>
            <i className="fas fa-plus"></i> New Parameter
          </button>
        </div>
      ) : (
        <div className="monitoring-list">
          {parameters.map((parameter) => (
            <div key={parameter.id} className="monitoring-card">
              <div className="monitoring-card-main">
                <h4>{parameter.name}{parameter.unit ? ` (${parameter.unit})` : ''}</h4>
                <div className="monitoring-card-meta">
                  <span className="category-badge">{formatCategory(parameter.category)}</span>
                  <span><i className="fas fa-redo"></i> {parameter.frequency.replace('_', ' ')}</span>
                  <span><i className="fas fa-project-diagram"></i> {parameter.project?.name || 'Org-wide'}</span>
                  {parameter.dataType === 'number' && (parameter.targetMin !== null || parameter.targetMax !== null) && (
                    <span><i className="fas fa-ruler"></i> Range: {parameter.targetMin ?? '—'} to {parameter.targetMax ?? '—'}</span>
                  )}
                  <span>
                    <i className="fas fa-user-check"></i>{' '}
                    {parameter.assignee ? `${parameter.assignee.firstName} ${parameter.assignee.lastName}` : 'Unassigned (any contributor)'}
                  </span>
                  <span
                    className={`rag-badge rag-${SEVERITY_TO_RAG_CLASS[parameter.defaultSeverity || 'major']}`}
                    title={SEVERITY_GUIDANCE[parameter.defaultSeverity || 'major']}
                  >
                    {(parameter.defaultSeverity || 'major').charAt(0).toUpperCase() + (parameter.defaultSeverity || 'major').slice(1)} if non-compliant
                  </span>
                  {!parameter.isActive && <span className="status-badge status-not_applicable">Inactive</span>}
                </div>
              </div>
              <div className="monitoring-card-actions">
                <button className="btn-icon" title="Edit" onClick={() => openEditModal(parameter)}>
                  <i className="fas fa-edit"></i>
                </button>
                <button className="btn-icon" title={parameter.isActive ? 'Deactivate' : 'Reactivate'} onClick={() => handleToggleActive(parameter)}>
                  <i className={`fas ${parameter.isActive ? 'fa-pause' : 'fa-play'}`}></i>
                </button>
                <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(parameter)}>
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="monitoring-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-sliders-h"></i> {form.id ? 'Edit' : 'New'} Monitoring Parameter</h2>
              <button className="close-btn" onClick={closeModal}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                {!form.id && (
                  <p className="modal-intro-hint">
                    Once saved, this shows up as a checklist item whenever someone inspects this category, and/or
                    somewhere to log a reading in Daily Log.
                  </p>
                )}
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Parameter name *</label>
                    <input type="text" value={form.name} onChange={handleChange('name')} placeholder="Enter parameter name" required />
                  </div>

                  <div className="form-group">
                    <label>Category *</label>
                    <select value={form.category} onChange={handleCategoryChange}>
                      {categoryOptions.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                      <option value="custom">Custom category...</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Default severity if non-compliant</label>
                    <select value={form.defaultSeverity} onChange={handleChange('defaultSeverity')}>
                      <option value="">Not set (defaults to Major)</option>
                      {SEVERITIES.map((s) => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </select>
                    <small className="form-hint">
                      {form.defaultSeverity
                        ? SEVERITY_GUIDANCE[form.defaultSeverity]
                        : 'Suggested based on category - used to weight this parameter\'s findings when it\'s checked during an inspection. You can always override it per finding later.'}
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Project scope</label>
                    <select value={form.projectId} onChange={handleChange('projectId')}>
                      <option value="">Org-wide (applies to all projects)</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {form.category === 'custom' && (
                    <div className="form-group full-width">
                      <label>Custom category name *</label>
                      <input type="text" value={form.customCategory} onChange={handleChange('customCategory')} placeholder="e.g. tailings" required />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Frequency</label>
                    <select value={form.frequency} onChange={handleChange('frequency')}>
                      {FREQUENCIES.map((f) => (
                        <option key={f} value={f}>{f.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Data type</label>
                    <select value={form.dataType} onChange={handleChange('dataType')}>
                      {DATA_TYPES.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Assign to</label>
                    <select value={form.assignedTo} onChange={handleChange('assignedTo')}>
                      <option value="">Unassigned - open to any contributor</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.firstName} {m.lastName} ({getRoleInfo(m.role, m.customRoleTitle).label})
                        </option>
                      ))}
                    </select>
                    <small className="form-hint">Only the assigned person (or a manager/admin) can submit data for this parameter.</small>
                  </div>

                  {form.dataType === 'number' && (
                    <div className="form-group full-width">
                      <label>Unit</label>
                      <input type="text" value={form.unit} onChange={handleChange('unit')} placeholder="e.g. dB, mg/L, ppm" />
                    </div>
                  )}

                  {form.dataType === 'number' && (
                    <>
                      <div className="form-group">
                        <label>Target min (optional)</label>
                        <input type="number" step="any" value={form.targetMin} onChange={handleChange('targetMin')} placeholder="No lower limit" />
                      </div>
                      <div className="form-group">
                        <label>Target max (optional)</label>
                        <input type="number" step="any" value={form.targetMax} onChange={handleChange('targetMax')} placeholder="No upper limit" />
                      </div>
                    </>
                  )}

                  {form.dataType === 'select' && (
                    <div className="form-group full-width">
                      <label>Options (comma-separated)</label>
                      <input type="text" value={form.options} onChange={handleChange('options')} placeholder="e.g. Good, Fair, Poor" />
                    </div>
                  )}

                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea rows="2" value={form.description} onChange={handleChange('description')} placeholder="What this parameter measures and how" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary-green" disabled={saving}>
                  {saving ? 'Saving...' : form.id ? 'Save Changes' : 'Create Parameter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringParameters;
